import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Minus, Plus, Lock, PenLine, ClipboardCheck } from "lucide-react";
import { initials, strokesReceived, stablefordPoints, dayHandicap } from "@/lib/scoring";

export default function ScoreTab({ event, currentDay, groupIdx, setGroupIdx, holeIdx, setHoleIdx, updateScores, onOpenSummary, scorerId, setScorerId, canEdit = true }) {
  const course = currentDay ? event.courses[currentDay.courseKey] : null;
  const hasGroups = !!(currentDay && currentDay.groups && currentDay.groups.length > 0);
  const gIdx = hasGroups ? Math.min(groupIdx, currentDay.groups.length - 1) : 0;
  const group = hasGroups ? currentDay.groups[gIdx] : null;
  const scorerInGroup = !!(group && scorerId && group.playerIds.includes(scorerId));
  const submitted = !!group?.submitted;
  const attestedCount = group ? group.playerIds.filter((pid) => group.attestations?.[pid]).length : 0;
  const myAttested = !!(scorerInGroup && group.attestations?.[scorerId]);

  // Swipe gesture state
  const touchStart = useRef(null);
  const swipeDxRef = useRef(0);
  const [swipeHint, setSwipeHint] = useState(null); // 'left' | 'right' | null
  const [swipeDx, setSwipeDx] = useState(0);
  const SWIPE_THRESHOLD = 60; // px
  const IGNORE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT", "BUTTON"]);

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    let el = e.target;
    while (el && el !== e.currentTarget) {
      if (IGNORE_TAGS.has(el.tagName)) { touchStart.current = null; return; }
      if (el.getAttribute && el.getAttribute("role") === "group") { touchStart.current = null; return; }
      el = el.parentElement;
    }
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    swipeDxRef.current = 0;
    setSwipeDx(0);
  };
  const onTouchMove = (e) => {
    if (!touchStart.current || e.touches.length !== 1) return;
    const t = e.touches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
      swipeDxRef.current = dx;
      setSwipeDx(dx);
      setSwipeHint(dx < 0 ? "left" : "right");
    }
  };
  const onTouchEnd = () => {
    if (!touchStart.current) return;
    const dx = swipeDxRef.current;
    const elapsed = Date.now() - touchStart.current.t;
    touchStart.current = null;
    swipeDxRef.current = 0;
    setSwipeDx(0);
    setSwipeHint(null);
    if (Math.abs(dx) < SWIPE_THRESHOLD || elapsed > 800) return;
    if (dx < 0 && holeIdx < 17) setHoleIdx(holeIdx + 1);
    else if (dx > 0 && holeIdx > 0) setHoleIdx(holeIdx - 1);
  };

  // The active scorer sees THEIR card (independent verification); without a scorer, the official score.
  const myStrokes = (pid, h) => (scorerInGroup ? group.cards?.[scorerId]?.[pid]?.[h] ?? null : group.scores?.[pid]?.[h]?.strokes ?? null);

  const holeStatus = useMemo(() => {
    if (!group) return [];
    return Array.from({ length: 18 }, (_, i) => {
      const allDone = group.playerIds.every((pid) => myStrokes(pid, i));
      const anyDone = group.playerIds.some((pid) => myStrokes(pid, i));
      return allDone ? "done" : anyDone ? "partial" : "empty";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, scorerId, scorerInGroup]);

  // Score edits are granular ops (offline-safe, merge-friendly). Auto-GIR when strokes - putts <= par - 2.
  const setScore = (playerId, field, val) => {
    if (submitted) { toast.error("Card submitted — ask the organiser to reopen this group"); return; }
    if (field === "strokes" && !scorerInGroup) { toast.error("Tap your name under “Scoring as” first"); return; }
    const prev = group?.scores?.[playerId]?.[holeIdx] || { strokes: null, fairway: null, gir: null, putts: null };
    const holeScore = { ...prev, [field]: val };
    const parNow = course?.par?.[holeIdx];
    // GIR auto-derives from the formula (strokes − putts ≤ par − 2) whenever strokes and putts are both known.
    // A manual GIR tap sticks until strokes or putts change again.
    if (field !== "gir" && parNow && holeScore.strokes && typeof holeScore.putts === "number") {
      holeScore.gir = holeScore.strokes - holeScore.putts <= parNow - 2;
    }
    updateScores([{ dayId: currentDay.id, groupIdx: gIdx, playerId, holeIdx, hole: holeScore, ...(field === "strokes" ? { scorerId } : {}) }]);
  };

  // All 18 official holes in for every group player (drives the sign-off banner)
  const groupComplete = useMemo(() => {
    if (!group) return false;
    for (const pid of group.playerIds) {
      for (let i = 0; i < 18; i++) if (!group.scores?.[pid]?.[i]?.strokes) return false;
    }
    return true;
  }, [group]);

  if (!currentDay) return <div className="tga-card-dark text-center py-8 helptext" data-testid="score-empty-state">{canEdit ? "Add a day first (Setup tab)." : "The organiser hasn't added a day yet — check back soon."}</div>;
  if (!course) return <div className="tga-card-dark text-center py-8 helptext">{canEdit ? "Select a course in Setup." : "The organiser still needs to pick a course for this day."}</div>;
  if (!hasGroups) return <div className="tga-card-dark text-center py-8 helptext">{canEdit ? "Assign groups in Setup." : "The organiser hasn't assigned groups for this day yet."}</div>;

  const par = course.par[holeIdx];
  const si = course.si[holeIdx];

  const setBbb = (kind, playerId) => {
    if (submitted) { toast.error("Card submitted — ask the organiser to reopen this group"); return; }
    const prev = group?.bbb?.[holeIdx] || { bingo: null, bango: null, bongo: null };
    updateScores([{ dayId: currentDay.id, groupIdx: gIdx, holeIdx, bbb: { ...prev, [kind]: playerId } }]);
  };

  const step = (playerId, field, delta, min = 0, max = 20) => {
    const cur = (field === "strokes" ? myStrokes(playerId, holeIdx) : group.scores?.[playerId]?.[holeIdx]?.[field]) || 0;
    const next = Math.max(min, Math.min(max, cur + delta));
    setScore(playerId, field, next);
  };

  const bbb = group.bbb?.[holeIdx] || {};

  return (
    <div
      className="space-y-4 tga-fade select-none"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{ transform: swipeDx ? `translateX(${Math.max(-40, Math.min(40, swipeDx / 3))}px)` : undefined, transition: swipeDx ? "none" : "transform .2s ease" }}
    >
      {/* Group selector */}
      {currentDay.groups.length > 1 && (
        <div className="flex gap-2 overflow-x-auto">
          {currentDay.groups.map((g, i) => (
            <button
              key={i}
              data-testid={`score-group-chip-${i}`}
              className={`chip ${i === groupIdx ? "active" : ""}`}
              onClick={() => setGroupIdx(i)}
            >
              Group {String.fromCharCode(65 + i)}
            </button>
          ))}
        </div>
      )}

      {/* Round status banner */}
      {submitted ? (
        <button data-testid="score-submitted-banner" onClick={onOpenSummary} className="w-full text-left rounded-xl border border-emerald-500/40 bg-emerald-900/30 p-3 flex items-center gap-3">
          <Lock size={18} className="text-emerald-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-emerald-100">Card submitted · Group {String.fromCharCode(65 + gIdx)}</div>
            <div className="text-[11px] text-emerald-200/80">Scores are locked. Tap to view signatures.</div>
          </div>
          <ChevronRight size={16} className="text-emerald-300" />
        </button>
      ) : groupComplete ? (
        <button data-testid="score-complete-banner" onClick={onOpenSummary} className="w-full text-left rounded-xl border border-[color:var(--brass)] bg-[rgba(197,160,89,0.12)] p-3 flex items-center gap-3">
          <PenLine size={18} className="text-[color:var(--brass-bright)] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[color:var(--brass-bright)]">All 18 holes in · {attestedCount}/{group.playerIds.length} signed</div>
            <div className="text-[11px] text-[color:var(--muted-green)]">{myAttested ? "You've signed — waiting on the rest of the group." : "Tap to sign & submit your card."}</div>
          </div>
          <ChevronRight size={16} className="text-[color:var(--brass-bright)]" />
        </button>
      ) : null}
      <div className={`tga-card ${scorerInGroup ? "" : "border-2 border-dashed border-[color:var(--brass)]"}`} data-testid="scorer-select-card" role="group">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wide text-[color:var(--brass-dark)]">Scoring as</div>
          {!scorerInGroup && <div className="text-xs text-[color:var(--brass-dark)]">Tap your name to start your card</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {group.playerIds.map((pid) => {
            const p = event.players.find((x) => x.id === pid);
            if (!p) return null;
            const active = pid === scorerId;
            return (
              <button
                key={pid}
                data-testid={`score-scorer-${pid}`}
                onClick={() => setScorerId(pid)}
                className={`inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border transition ${active ? "bg-[color:var(--brass)] border-[color:var(--brass)] text-[#14251b] font-semibold" : "border-[rgba(27,51,34,0.2)]"}`}
              >
                <span className="avatar" style={{ width: 24, height: 24, fontSize: 11 }}>{initials(p.name)}</span>
                <span className="text-sm">{p.name}</span>
              </button>
            );
          })}
        </div>
        {scorerInGroup && <p className="helptext text-[color:var(--text-dark)]/60 mt-2">You're keeping a card for the whole group. Every player's card must match before the day is finalised.</p>}
      </div>

      {/* Hole header */}
      <div className="tga-card-dark relative overflow-hidden">
        {swipeHint === "left" && holeIdx < 17 && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--brass-bright)] opacity-80 pointer-events-none">
            <ChevronRight size={40} />
          </div>
        )}
        {swipeHint === "right" && holeIdx > 0 && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--brass-bright)] opacity-80 pointer-events-none">
            <ChevronLeft size={40} />
          </div>
        )}
        <div className="flex items-center gap-3">
          <button data-testid="score-hole-prev" disabled={holeIdx === 0} aria-label="Previous hole" className="ghost-btn rounded-xl p-3 min-w-[52px] min-h-[52px] inline-flex items-center justify-center disabled:opacity-30" onClick={() => setHoleIdx(Math.max(0, holeIdx - 1))}>
            <ChevronLeft size={28} />
          </button>
          <div className="flex-1 text-center">
            <div className="text-[color:var(--muted-green)] text-xs uppercase tracking-widest">Hole</div>
            <div className="font-display text-4xl text-[color:var(--brass-bright)]">{holeIdx + 1}</div>
            <div className="text-sm text-[color:var(--muted-green)]">Par {par} · SI {si}</div>
            <div className="text-[10px] text-[color:var(--muted-green)]/70 mt-0.5 tracking-wide hidden sm:block">swipe or tap to change hole</div>
            <div className="text-[10px] text-[color:var(--muted-green)]/70 mt-0.5 tracking-wide sm:hidden">← swipe to change hole →</div>
          </div>
          <button data-testid="score-hole-next" disabled={holeIdx === 17} aria-label="Next hole" className="brass-btn rounded-xl p-3 min-w-[52px] min-h-[52px] inline-flex items-center justify-center disabled:opacity-30" onClick={() => setHoleIdx(Math.min(17, holeIdx + 1))}>
            <ChevronRight size={28} />
          </button>
        </div>
        <div className="flex gap-1 mt-3 justify-center flex-wrap">
          {holeStatus.map((s, i) => (
            <button key={i} onClick={() => setHoleIdx(i)} data-testid={`score-hole-pip-${i}`} className={`hole-pip ${s === "done" ? "done" : ""} ${i === holeIdx ? "active" : ""}`} aria-label={`Hole ${i+1}`} />
          ))}
        </div>
      </div>

      {/* Player rows */}
      <div className={`space-y-3 ${submitted ? "opacity-60" : ""}`} data-testid="score-player-rows">
        {group.playerIds.map((pid) => {
          const p = event.players.find((x) => x.id === pid);
          if (!p) return null;
          const score = { ...(group.scores?.[pid]?.[holeIdx] || {}), strokes: myStrokes(pid, holeIdx) };
          const hcp = dayHandicap(currentDay, p);
          const rec = strokesReceived(hcp, si);
          const points = stablefordPoints(score.strokes, par, hcp, si);
          const par3 = par === 3;
          return (
            <div key={pid} className="tga-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="avatar">{initials(p.name)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg leading-tight truncate">{p.name}</div>
                  <div className="text-xs text-[color:var(--brass-dark)]">HCP {hcp} · +{rec}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-[color:var(--brass-dark)]">Points</div>
                  <div className="font-mono-metrics text-2xl font-bold leading-none">{points}</div>
                </div>
              </div>

              {/* Strokes — prominent stepper + quick chips */}
              <div className="mb-3">
                <div className="flex items-baseline justify-between mb-1">
                  <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-dark)]/60 font-semibold">Strokes</div>
                  <div className="text-[11px] text-[color:var(--brass-dark)] font-mono-metrics">Par {par}</div>
                </div>
                <div className="big-stepper" role="group" aria-label={`Strokes for ${p.name}`}>
                  <button aria-label="Decrease strokes" data-testid={`score-strokes-minus-${pid}`} onClick={() => step(pid, "strokes", -1, 1, 15)} disabled={!score.strokes}>
                    <Minus size={22} />
                  </button>
                  <div className="value" data-testid={`score-strokes-value-${pid}`}>
                    <span>{score.strokes ?? "—"}</span>
                    {score.strokes ? (
                      <span className="relpar">{
                        (() => {
                          const d = score.strokes - par;
                          if (d === 0) return "PAR";
                          if (d === -1) return "BIRDIE";
                          if (d === -2) return "EAGLE";
                          if (d <= -3) return "ALBATROSS";
                          if (d === 1) return "BOGEY";
                          if (d === 2) return "DOUBLE";
                          if (d === 3) return "TRIPLE";
                          return `+${d}`;
                        })()
                      }</span>
                    ) : null}
                  </div>
                  <button aria-label="Increase strokes" data-testid={`score-strokes-plus-${pid}`} onClick={() => step(pid, "strokes", +1, 1, 15)}>
                    <Plus size={22} />
                  </button>
                </div>
                <div className="qs-row">
                  {[-1, 0, 1, 2].map((diff) => {
                    const v = Math.max(1, par + diff);
                    const label = diff === -1 ? "Birdie" : diff === 0 ? "Par" : diff === 1 ? "Bogey" : "Double";
                    const isSel = score.strokes === v;
                    return (
                      <button
                        key={diff}
                        data-testid={`score-strokes-quick-${pid}-${v}`}
                        className={`qs-chip ${isSel ? "selected" : ""}`}
                        onClick={() => setScore(pid, "strokes", v)}
                      >
                        <span className="label">{label}</span>
                        {v}
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const cards = group.cards || {};
                  const entries = group.playerIds.map((sid) => [sid, cards[sid]?.[pid]?.[holeIdx] ?? null]).filter(([, v]) => v);
                  if (entries.length === 0) return null;
                  const mismatch = new Set(entries.map(([, v]) => v)).size > 1;
                  return (
                    <div className={`mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] ${mismatch ? "text-red-700" : "text-[color:var(--text-dark)]/60"}`} data-testid={`score-cards-${pid}`}>
                      <span className="uppercase tracking-wide">{mismatch ? "Cards disagree" : "Cards"}</span>
                      {entries.map(([sid, v]) => {
                        const s = event.players.find((x) => x.id === sid);
                        return <span key={sid} className="font-mono-metrics rounded px-1.5 py-0.5 bg-[rgba(27,51,34,0.06)]">{initials(s?.name || "?")} {v}</span>;
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Putts — prominent stepper */}
              <div className="mb-3">
                <div className="flex items-baseline justify-between mb-1">
                  <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-dark)]/60 font-semibold">Putts</div>
                  {typeof score.putts === "number" && score.strokes ? (
                    <div className="text-[11px] text-[color:var(--brass-dark)] font-mono-metrics">
                      {score.strokes - score.putts} to green
                    </div>
                  ) : null}
                </div>
                <div className="big-stepper putts" role="group" aria-label={`Putts for ${p.name}`}>
                  <button aria-label="Decrease putts" data-testid={`score-putts-minus-${pid}`} onClick={() => step(pid, "putts", -1, 0, 10)} disabled={!(typeof score.putts === "number" && score.putts > 0)}>
                    <Minus size={22} />
                  </button>
                  <div className="value">
                    <span>{typeof score.putts === "number" ? score.putts : "—"}</span>
                  </div>
                  <button aria-label="Increase putts" data-testid={`score-putts-plus-${pid}`} onClick={() => step(pid, "putts", +1, 0, 10)}>
                    <Plus size={22} />
                  </button>
                </div>
                <div className="qs-row">
                  {[0, 1, 2, 3].map((v) => {
                    const isSel = score.putts === v;
                    return (
                      <button
                        key={v}
                        data-testid={`score-putts-quick-${pid}-${v}`}
                        className={`qs-chip ${isSel ? "selected" : ""}`}
                        onClick={() => setScore(pid, "putts", v)}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* FH / GIR — single toggle buttons */}
              <div className="flex items-center gap-3 flex-wrap">
                {!par3 && (
                  <button
                    data-testid={`score-fh-toggle-${pid}`}
                    className={`chk-toggle ${score.fairway === true ? "on" : ""}`}
                    onClick={() => setScore(pid, "fairway", score.fairway === true ? null : true)}
                    aria-pressed={score.fairway === true}
                  >
                    <span className="chk-tick" aria-hidden="true">{score.fairway === true ? "✓" : ""}</span>
                    Fairway hit
                  </button>
                )}
                <button
                  data-testid={`score-gir-toggle-${pid}`}
                  className={`chk-toggle ${score.gir === true ? "on" : ""}`}
                  onClick={() => setScore(pid, "gir", score.gir === true ? false : true)}
                  aria-pressed={score.gir === true}
                >
                  <span className="chk-tick" aria-hidden="true">{score.gir === true ? "✓" : ""}</span>
                  Green in reg.
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* BBB entry */}
      {currentDay.bbbEnabled && (
        <div className="tga-card">
          <div className="font-display text-lg mb-2">Bingo · Bango · Bongo</div>
          {["bingo","bango","bongo"].map((kind) => (
            <div key={kind} className="flex items-center justify-between mb-2">
              <div className="text-sm capitalize font-medium">{kind}</div>
              <select
                data-testid={`bbb-select-${kind}`}
                className="tga-input-light !w-40"
                value={bbb[kind] || ""}
                onChange={(e) => setBbb(kind, e.target.value || null)}
              >
                <option value="">—</option>
                {group.playerIds.map((pid) => {
                  const p = event.players.find(x => x.id === pid);
                  return p ? <option key={pid} value={pid}>{p.name}</option> : null;
                })}
              </select>
            </div>
          ))}
          <p className="helptext text-[color:var(--text-dark)]/60">Bingo=first on green · Bango=closest once all on · Bongo=first in cup</p>
        </div>
      )}

      {/* Big hole navigation */}
      <div className="flex gap-3">
        <button
          data-testid="score-prev-hole-big"
          disabled={holeIdx === 0}
          className="ghost-btn rounded-xl px-4 py-4 inline-flex items-center justify-center gap-1 disabled:opacity-30 min-w-[72px]"
          onClick={() => { setHoleIdx(Math.max(0, holeIdx - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        ><ChevronLeft size={22} /></button>
        {holeIdx === 17 ? (
          <button
            data-testid="score-sign-submit-btn"
            className={`${submitted ? "ghost-btn" : "brass-btn"} rounded-xl flex-1 py-4 text-lg font-display inline-flex items-center justify-center gap-2`}
            onClick={() => { onOpenSummary(); }}
          >{submitted ? <><Lock size={20} /> Card submitted</> : <><ClipboardCheck size={22} /> Sign & submit card</>}</button>
        ) : (
          <button
            data-testid="score-next-hole-big"
            className="brass-btn rounded-xl flex-1 py-4 text-lg font-display inline-flex items-center justify-center gap-2"
            onClick={() => { setHoleIdx(Math.min(17, holeIdx + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >{`Next hole · ${holeIdx + 2}`} <ChevronRight size={22} /></button>
        )}
      </div>
    </div>
  );
}
